import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Deck, OwnedCardData } from '../interfaces/index'
import { ENV } from '../config/env.js'
import i18n from '../i18n.js'
import { CardProgressionSystem, SKILL_RESET_COST_GEMS } from '../engine/CardProgressionSystem.js'
import { useSeasonStore } from './useSeasonStore.js'
import { AnalyticsService } from '../services/AnalyticsService.js'

// Re-export Deck from interfaces so consumers can import it from here
export type { Deck } from '../interfaces/index'

const MAX_DECKS = 5

// ---------------------------------------------------------------------------
// Daily Login Streak
// ---------------------------------------------------------------------------

export interface StreakReward {
  day: number
  gold: number
  gems: number
  xp: number
  label: string  // human-readable DE description
}

const STREAK_REWARDS: StreakReward[] = [
  { day: 1, gold: 100,  gems: 0,  xp: 50,  label: '100 Gold' },
  { day: 2, gold: 200,  gems: 0,  xp: 100, label: '200 Gold' },
  { day: 3, gold: 300,  gems: 5,  xp: 150, label: '300 Gold + 5 Gems' },
  { day: 4, gold: 200,  gems: 10, xp: 100, label: '200 Gold + 10 Gems' },
  { day: 5, gold: 500,  gems: 0,  xp: 200, label: '500 Gold' },
  { day: 6, gold: 300,  gems: 20, xp: 150, label: '300 Gold + 20 Gems' },
  { day: 7, gold: 1000, gems: 50, xp: 500, label: 'JACKPOT: 1000 Gold + 50 Gems!' },
]

// No DEFAULT_DECK — new players create their first deck during the tutorial.
// Exported so TutorialScreen can pre-populate the deck picker with starter card IDs.
export const STARTER_CARD_IDS = {
  towers: ['archer', 'firemage', 'frost', 'lightning', 'shadow', 'goldmine', 'mudpit', 'wall'],
  units: ['goblin', 'firespirit', 'vinewurm', 'crossbow'],
  heroId: 'merchant',
} as const

export const STARTER_UNLOCK_IDS: string[] = [
  'archer', 'goldmine', 'wall', 'frost',      // 4 towers
  'goblin', 'firespirit', 'crossbow', 'vinewurm', // 4 units
  'merchant',                                   // 1 hero
]

export interface UnopenedPack {
  packType: string
  purchasedAt: string  // ISO date string
}

/** Result from addCardCopies — tells the caller what changed. */
export interface CardCopyResult {
  newLevel: number
  prevLevel: number
  leveledUp: boolean
  /** Capstone nodes that were automatically unlocked due to the level-up. */
  autoUnlockedNodes: string[]
}

interface ProgressionState {
  xp: number
  level: number
  gold: number
  gems: number
  packMeter: number
  ownedCardIds: string[]
  /**
   * New progression-aware card store. Keys are card IDs.
   * Replaces the simple ownedCardIds set for cards that have been drawn at
   * least once through the new pack system. ownedCardIds is kept in sync.
   */
  ownedCards: Record<string, OwnedCardData>
  unlockedCardIds: string[]
  unlockCard: (cardId: string) => void
  decks: Deck[]
  selectedDeckId: string | null
  lastDailyPack: string  // ISO date string (UTC), empty string if never claimed
  lastWeeklyReset: string  // format 'YYYY-WXX', empty string if never reset
  streakDays: number          // current streak count (1-7)
  lastStreakDate: string | null  // ISO date string 'YYYY-MM-DD' of last login
  streakBroken: boolean        // true if today's login broke the streak (missed yesterday)
  questProgress: Array<{ questId: string; progress: number; completed: boolean; claimedReward: boolean }>
  pushPermissionRequested: boolean
  unopenedPacks: UnopenedPack[]
  language: 'de' | 'en' | 'fr'
  /**
   * Whether the player has completed (or explicitly skipped) the first-run tutorial.
   * Used to gate the TutorialScreen: on first app start this is false, which causes
   * App.tsx to navigate to 'tutorial' before reaching 'main-menu'.
   * Set to true on tutorial completion or skip — never reset automatically.
   */
  tutorialCompleted: boolean
  // actions
  addXp: (amount: number) => void
  addGold: (amount: number) => void
  spendGold: (amount: number) => boolean
  addGems: (n: number) => void
  setPackMeter: (value: number) => void
  addOwnedCards: (cardIds: string[]) => void
  /**
   * Add N copies of a card and return what changed (level, auto-unlocks).
   * Also grants XP and gold bonus on level-up.
   * `cardRarity` and `cardCategory` are used to compute the correct level threshold.
   */
  addCardCopies: (
    cardId: string,
    copies: number,
    cardRarity?: 'common' | 'rare' | 'epic' | 'legendary',
    cardCategory?: 'tower' | 'unit' | 'hero',
  ) => CardCopyResult
  /**
   * Unlock a skill tree node for a card. Returns false if the node is
   * not eligible (level too low, parent not chosen, etc.).
   */
  chooseSkillNode: (cardId: string, nodeId: string) => boolean
  /**
   * Reset all chosen skill tree nodes for a card. Costs SKILL_RESET_COST_GEMS gems.
   * Returns false if the player cannot afford it.
   */
  resetSkillTree: (cardId: string) => boolean
  saveDeck: (deck: Deck) => void
  deleteDeck: (deckId: string) => void
  selectDeck: (deckId: string) => void
  getActiveDeck: (id: string) => Deck | undefined
  setLastDailyPack: (date: string) => void
  updateQuestProgress: (states: Array<{ questId: string; progress: number; completed: boolean; claimedReward: boolean }>) => void
  claimQuestReward: (questId: string) => void
  setPushPermissionRequested: (v: boolean) => void
  resetWeeklyQuestsIfNeeded: (isoWeekStr: string) => void
  addUnopenedPack: (packType: string) => void
  removeUnopenedPack: (index: number) => void
  setLanguage: (lang: 'de' | 'en' | 'fr') => void
  completeTutorial: () => void
  /** Check if a new day has started and return the streak reward, or null if already claimed today. */
  checkDailyStreak: () => StreakReward | null
  /** Load progression from server and overwrite local state. Returns true on success. */
  syncFromServer: () => Promise<boolean>
  /** Save current progression state to server. Returns true on success. */
  saveToServer: () => Promise<boolean>
}

const XP_PER_LEVEL = 500

// Quest IDs that belong to the weekly reset cycle
const WEEKLY_QUEST_IDS = [
  'win_5_pvp',
  'weekly_wins_5',
  'weekly_units_50',
  'weekly_kills_100',
  'weekly_towers_30',
]

export const useProgressionStore = create<ProgressionState>()(
  persist(
    (set, get) => ({
      xp: 0,
      level: 1,
      gold: 200,
      gems: 0,
      packMeter: 0,
      ownedCardIds: [],
      ownedCards: {},
      unlockedCardIds: STARTER_UNLOCK_IDS,
      decks: [],
      selectedDeckId: null,
      lastDailyPack: '',
      lastWeeklyReset: '',
      streakDays: 0,
      lastStreakDate: null,
      streakBroken: false,
      questProgress: [],
      pushPermissionRequested: false,
      unopenedPacks: [],
      language: 'de' as const,
      tutorialCompleted: false,

      addXp: (amount) => {
        // Propagate XP to the season store so the battle pass progresses
        useSeasonStore.getState().addSeasonXp(amount)
        set((s) => {
          const newXp = s.xp + amount
          const newLevel = Math.floor(newXp / XP_PER_LEVEL) + 1
          if (newLevel > s.level) {
            AnalyticsService.getInstance().logLevelUp({ newLevel, totalXp: newXp })
          }
          return { xp: newXp, level: Math.max(s.level, newLevel) }
        })
      },

      addGold: (amount) => set((s) => ({ gold: s.gold + amount })),

      spendGold: (amount) => {
        const { gold } = get()
        if (gold < amount) return false
        set({ gold: gold - amount })
        return true
      },

      addGems: (n) => set((s) => ({ gems: s.gems + n })),

      setPackMeter: (value) => set({ packMeter: Math.min(100, Math.max(0, value)) }),

      unlockCard: (cardId) =>
        set((s) => {
          if (s.unlockedCardIds.includes(cardId)) return s
          return { unlockedCardIds: [...s.unlockedCardIds, cardId] }
        }),

      addOwnedCards: (cardIds) =>
        set((s) => {
          const newIds = Array.from(new Set([...s.ownedCardIds, ...cardIds]))
          const newOwnedCards = { ...s.ownedCards }
          for (const id of cardIds) {
            if (!(id in newOwnedCards)) {
              newOwnedCards[id] = { copies: 1, chosenNodes: [] }
            }
          }
          return {
            ownedCardIds: newIds,
            ownedCards: newOwnedCards,
            unlockedCardIds: Array.from(new Set([...s.unlockedCardIds, ...cardIds])),
          }
        }),

      addCardCopies: (cardId, copies, cardRarity = 'common', cardCategory = 'tower') => {
        const state = get()
        const existing = state.ownedCards[cardId] ?? { copies: 0, chosenNodes: [] }
        const progression = CardProgressionSystem.getInstance()
        const prevLevel = progression.getCardLevel(existing.copies, cardRarity)
        const newCopies = existing.copies + copies
        const newLevel  = progression.getCardLevel(newCopies, cardRarity)

        // Auto-unlock capstone nodes that become available at the new level
        const autoNodes = progression.getAutoNodes(
          cardId, cardCategory, newCopies, cardRarity, existing.chosenNodes,
        )
        const autoIds = autoNodes.map((n) => n.id)
        const updatedNodes = Array.from(new Set([...existing.chosenNodes, ...autoIds]))

        const updatedCard: OwnedCardData = { copies: newCopies, chosenNodes: updatedNodes }

        set((s) => ({
          ownedCards: { ...s.ownedCards, [cardId]: updatedCard },
          ownedCardIds: s.ownedCardIds.includes(cardId)
            ? s.ownedCardIds
            : [...s.ownedCardIds, cardId],
        }))

        // Grant level-up rewards
        if (newLevel > prevLevel) {
          const xpReward  = (newLevel - prevLevel) * 100
          const goldReward = (newLevel - prevLevel) * 50
          get().addXp(xpReward)
          get().addGold(goldReward)
        }

        return {
          newLevel,
          prevLevel,
          leveledUp: newLevel > prevLevel,
          autoUnlockedNodes: autoIds,
        }
      },

      chooseSkillNode: (cardId, nodeId) => {
        const state = get()
        const existing = state.ownedCards[cardId]
        if (existing === undefined) return false
        if (existing.chosenNodes.includes(nodeId)) return true  // already chosen

        // Validate via CardProgressionSystem (pending gate check not required here;
        // the UI already shows only eligible nodes)
        const updatedNodes = [...existing.chosenNodes, nodeId]
        set((s) => ({
          ownedCards: {
            ...s.ownedCards,
            [cardId]: { ...existing, chosenNodes: updatedNodes },
          },
        }))
        return true
      },

      resetSkillTree: (cardId) => {
        const state = get()
        const existing = state.ownedCards[cardId]
        if (existing === undefined) return false
        if (state.gems < SKILL_RESET_COST_GEMS) return false

        set((s) => ({
          gems: s.gems - SKILL_RESET_COST_GEMS,
          ownedCards: {
            ...s.ownedCards,
            [cardId]: { ...existing, chosenNodes: [] },
          },
        }))
        return true
      },

      saveDeck: (deck) => {
        set((s) => {
          const existing = s.decks.findIndex((d) => d.id === deck.id)
          if (existing >= 0) {
            const decks = [...s.decks]
            decks[existing] = deck
            return { decks }
          }
          if (s.decks.length >= MAX_DECKS) {
            console.warn('[ProgressionStore] Max deck limit reached (5). Cannot save new deck.')
            return {}
          }
          return { decks: [...s.decks, deck] }
        })
        void get().saveToServer()
      },

      deleteDeck: (deckId) => {
        set((s) => ({
          decks: s.decks.filter((d) => d.id !== deckId),
          selectedDeckId: s.selectedDeckId === deckId ? null : s.selectedDeckId,
        }))
        void get().saveToServer()
      },

      selectDeck: (deckId) => set({ selectedDeckId: deckId }),

      getActiveDeck: (id: string) => {
        return get().decks.find((d) => d.id === id)
      },

      setLastDailyPack: (date: string) => set({ lastDailyPack: date }),

      updateQuestProgress: (states) =>
        set((s) => {
          const map = new Map(s.questProgress.map((q) => [q.questId, q]))
          for (const state of states) {
            map.set(state.questId, state)
          }
          return { questProgress: Array.from(map.values()) }
        }),

      claimQuestReward: (questId) => {
        set((s) => ({
          questProgress: s.questProgress.map((q) =>
            q.questId === questId ? { ...q, claimedReward: true } : q
          ),
        }))
        void get().saveToServer()
      },

      setPushPermissionRequested: (v: boolean) => set({ pushPermissionRequested: v }),

      resetWeeklyQuestsIfNeeded: (isoWeekStr: string) => {
        const { lastWeeklyReset } = get()
        if (isoWeekStr === lastWeeklyReset) return
        set((s) => ({
          lastWeeklyReset: isoWeekStr,
          questProgress: s.questProgress.map((q) =>
            WEEKLY_QUEST_IDS.includes(q.questId)
              ? { questId: q.questId, progress: 0, completed: false, claimedReward: false }
              : q
          ),
        }))
      },

      addUnopenedPack: (packType: string) =>
        set((s) => ({
          unopenedPacks: [
            ...s.unopenedPacks,
            { packType, purchasedAt: new Date().toISOString() },
          ],
        })),

      removeUnopenedPack: (index: number) =>
        set((s) => ({
          unopenedPacks: s.unopenedPacks.filter((_, i) => i !== index),
        })),

      setLanguage: (lang: 'de' | 'en' | 'fr') => {
        set({ language: lang })
        void i18n.changeLanguage(lang)
      },

      checkDailyStreak: (): StreakReward | null => {
        const state = get()
        const today = new Date().toISOString().split('T')[0]  // 'YYYY-MM-DD'
        if (state.lastStreakDate === today) return null  // already claimed today

        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]
        const consecutive = state.lastStreakDate === yesterday
        const newStreak = consecutive ? Math.min(state.streakDays + 1, 7) : 1
        const broken = state.lastStreakDate !== null && !consecutive

        const reward = STREAK_REWARDS[newStreak - 1]

        set({ streakDays: newStreak, lastStreakDate: today, streakBroken: broken })

        // Apply rewards
        if (reward.gold > 0) get().addGold(reward.gold)
        if (reward.gems > 0) get().addGems(reward.gems)
        if (reward.xp > 0) get().addXp(reward.xp)

        return reward
      },

      /**
       * Mark the tutorial as completed and grant the one-time reward.
       * Called from TutorialScreen on the final step — also triggers the
       * 'complete_tutorial' achievement quest so the achievement shows in
       * ProgressionScreen without a separate event path.
       */
      syncFromServer: async () => {
        const { useAuthStore } = await import('./useAuthStore.js')
        const session = useAuthStore.getState().session
        if (!session) return false

        const doFetch = (token: string) =>
          fetch('/api/profile/progression', { headers: { Authorization: `Bearer ${token}` } })

        try {
          let res = await doFetch(session.token.accessToken)
          if (res.status === 401) {
            const ok = await useAuthStore.getState().refreshSession()
            if (!ok) return false
            res = await doFetch(useAuthStore.getState().session?.token.accessToken ?? '')
          }
          if (!res.ok) return false
          const data = await res.json() as Record<string, unknown>
          set({
            xp:                     typeof data['xp']      === 'number' ? data['xp']      : 0,
            level:                  typeof data['level']   === 'number' ? data['level']   : 1,
            gold:                   typeof data['gold']    === 'number' ? data['gold']    : 200,
            gems:                   typeof data['gems']    === 'number' ? data['gems']    : 0,
            packMeter:              typeof data['packMeter'] === 'number' ? data['packMeter'] : 0,
            ownedCardIds:           Array.isArray(data['ownedCardIds']) ? data['ownedCardIds'] as string[] : [],
            ownedCards:             (data['ownedCards'] && typeof data['ownedCards'] === 'object') ? data['ownedCards'] as Record<string, import('../interfaces/index').OwnedCardData> : {},
            decks:                  Array.isArray(data['decks']) ? data['decks'] as Deck[] : [],
            selectedDeckId:         typeof data['selectedDeckId'] === 'string' ? data['selectedDeckId'] : null,
            lastDailyPack:          typeof data['lastDailyPack']  === 'string' ? data['lastDailyPack']  : '',
            lastWeeklyReset:        typeof data['lastWeeklyReset'] === 'string' ? data['lastWeeklyReset'] : '',
            pushPermissionRequested: data['pushPermissionRequested'] === true,
            questProgress:          Array.isArray(data['questProgress']) ? data['questProgress'] as ProgressionState['questProgress'] : [],
            tutorialCompleted:      data['tutorialCompleted'] === true,
            unopenedPacks:          Array.isArray(data['unopenedPacks']) ? data['unopenedPacks'] as UnopenedPack[] : [],
          })
          if (typeof data['language'] === 'string') void i18n.changeLanguage(data['language'])
          return true
        } catch {
          return false
        }
      },

      saveToServer: async () => {
        const { useAuthStore } = await import('./useAuthStore.js')
        const session = useAuthStore.getState().session
        if (!session) return false

        const s = get()
        const body = {
          xp: s.xp, level: s.level, gold: s.gold, gems: s.gems, packMeter: s.packMeter,
          ownedCardIds: s.ownedCardIds, ownedCards: s.ownedCards, decks: s.decks,
          selectedDeckId: s.selectedDeckId, lastDailyPack: s.lastDailyPack,
          lastWeeklyReset: s.lastWeeklyReset, pushPermissionRequested: s.pushPermissionRequested,
          language: s.language, questProgress: s.questProgress,
          tutorialCompleted: s.tutorialCompleted, unopenedPacks: s.unopenedPacks,
        }

        const doFetch = (token: string) =>
          fetch('/api/profile/progression', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
          })

        try {
          let res = await doFetch(session.token.accessToken)
          if (res.status === 401) {
            const ok = await useAuthStore.getState().refreshSession()
            if (!ok) return false
            res = await doFetch(useAuthStore.getState().session?.token.accessToken ?? '')
          }
          return res.ok
        } catch {
          return false
        }
      },

      completeTutorial: () => {
        const { addXp, addGold, addUnopenedPack, updateQuestProgress, questProgress } = get()
        set({ tutorialCompleted: true })
        addXp(150)
        addGold(200)
        addUnopenedPack('starter')
        // Mark tutorial achievement quest as completed so it shows in ProgressionScreen
        const existing = questProgress.find((q) => q.questId === 'complete_tutorial')
        if (!existing || !existing.completed) {
          const updated = questProgress.filter((q) => q.questId !== 'complete_tutorial')
          updateQuestProgress([
            ...updated,
            { questId: 'complete_tutorial', progress: 1, completed: true, claimedReward: false },
          ])
        }
      },
    }),
    {
      name: 'ultimaze-progression',
      onRehydrateStorage: () => (state) => {
        // No forced deck migration — new players create their first deck during tutorial.
        // Migration: ensure unopenedPacks exists for users who had store without this field.
        if (state !== undefined && state.unopenedPacks === undefined) {
          state.unopenedPacks = []
        }
        // Migration: ensure ownedCards exists and is populated from ownedCardIds.
        if (state !== undefined) {
          if (state.ownedCards === undefined) {
            state.ownedCards = {}
          }
          // Back-fill any card IDs that exist in the old ownedCardIds but not in ownedCards
          for (const id of state.ownedCardIds) {
            if (!(id in state.ownedCards)) {
              state.ownedCards[id] = { copies: 1, chosenNodes: [] }
            }
          }
        }
        // DEV ONLY — reset daily pack timer on every app start for testing. Remove before release.
        if (state !== undefined && ENV.IS_DEV) {
          state.lastDailyPack = ''
        }
      },
    }
  )
)
